import { useState, useEffect } from "react";
import api from "../../utils/api";
import UploadImage from "../UploadImage";
import { HiPlus, HiTrash } from "react-icons/hi";

export default function AddContract({ contentOwnerId, productId, onSuccess }) {
  const [contractName, setContractName] = useState("");
  const [signingDate, setSigningDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [selectedProductId, setSelectedProductId] = useState(productId || "");
  const [selectedContentOwnerId, setSelectedContentOwnerId] = useState(
    contentOwnerId || "",
  );
  const [artworks, setArtworks] = useState([{ value: "" }]);
  const [images, setImages] = useState([{ file: null, preview: null }]);
  const [products, setProducts] = useState([]);
  const [contentOwners, setContentOwners] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await api.get("/users/me/products", {
          params: { limit: 100 },
          withCredentials: true,
        });
        setProducts(res.data.products || []);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    if (!selectedProductId) {
      setContentOwners([]);
      setSelectedContentOwnerId("");
      return;
    }

    const fetchContentOwners = async () => {
      try {
        const res = await api.get(
          `/contentOwners/products/${selectedProductId}`,
          { params: { limit: 100 }, withCredentials: true },
        );
        setContentOwners(res.data.contentOwners || []);
      } catch (error) {
        console.error("Error fetching content owners:", error);
        setContentOwners([]);
      }
    };

    fetchContentOwners();
  }, [selectedProductId]);

  const addArtworkRow = () => setArtworks([...artworks, { value: "" }]);
  const removeArtworkRow = (index) => {
    if (artworks.length === 1) return; // always at least one
    setArtworks(artworks.filter((_, i) => i !== index));
  };
  const updateArtwork = (index, value) => {
    const updated = [...artworks];
    updated[index].value = value;
    setArtworks(updated);
  };

  const addImageRow = () =>
    setImages([...images, { file: null, preview: null }]);
  const removeImageRow = (index) => {
    if (images.length === 1) return; // always at least one
    setImages(images.filter((_, i) => i !== index));
  };
  const handleImageSelect = (index, file) => {
    const updated = [...images];
    updated[index] = { file, preview: URL.createObjectURL(file) };
    setImages(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!contractName || !signingDate || !expiryDate)
      return alert("Please fill all required fields.");
    if (!selectedProductId) return alert("Please select a product.");
    if (!selectedContentOwnerId) return alert("Please select a content owner.");
    if (artworks.some((a) => !a.value.trim()))
      return alert("All artwork fields must be filled.");
    if (images.some((img) => !img.file))
      return alert("Please upload at least one contract image.");

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("contractName", contractName);
      formData.append("signingDate", signingDate);
      formData.append("expiryDate", expiryDate);
      formData.append("contentOwnerId", selectedContentOwnerId);
      formData.append("productId", selectedProductId);
      formData.append(
        "artworks",
        JSON.stringify(artworks.map((a) => a.value.trim())),
      );
      images.forEach((img) => formData.append("contractImageUrl", img.file));

      await api.post("/contracts", formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("Contract added successfully!");
      onSuccess?.();
    } catch (error) {
      console.error(error.response?.data || error.message);
      alert(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-md mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
    >
      <h2 className="text-xl font-semibold text-gray-800 mb-6">Add Contract</h2>

      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-600 mb-1">
          Contract Name
        </label>
        <input
          type="text"
          placeholder="Enter contract name"
          value={contractName}
          onChange={(e) => setContractName(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-600 mb-1">
          Signing Date
        </label>
        <input
          type="date"
          value={signingDate}
          onChange={(e) => setSigningDate(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-600 mb-1">
          Expiry Date
        </label>
        <input
          type="date"
          value={expiryDate}
          onChange={(e) => setExpiryDate(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-600 mb-1">
          Choose Product
        </label>
        <select
          value={selectedProductId}
          onChange={(e) => setSelectedProductId(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
        >
          <option value="">Select a product</option>
          {products.map((product) => (
            <option key={product._id} value={product._id}>
              {product.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-600 mb-1">
          Choose Content Owner
        </label>
        <select
          value={selectedContentOwnerId}
          onChange={(e) => setSelectedContentOwnerId(e.target.value)}
          disabled={!selectedProductId}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500"
        >
          <option value="">
            {selectedProductId
              ? "Select a content owner"
              : "Select a product first"}
          </option>
          {contentOwners.map((owner) => (
            <option key={owner._id} value={owner._id}>
              {owner.email}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-6 space-y-4">
        <label className="block text-sm font-medium text-gray-600">
          Artworks
        </label>
        {artworks.map((artwork, index) => (
          <div key={index} className="flex items-center gap-3">
            <input
              type="text"
              placeholder={`Artwork ${index + 1}`}
              value={artwork.value}
              onChange={(e) => updateArtwork(index, e.target.value)}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
            />
            {artworks.length > 1 && (
              <button
                type="button"
                onClick={() => removeArtworkRow(index)}
                className="text-red-500 hover:text-red-700"
              >
                <HiTrash className="w-5 h-5" />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addArtworkRow}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
        >
          <HiPlus className="w-4 h-4" /> Add artwork
        </button>
      </div>

      <div className="mb-6 space-y-4">
        <label className="block text-sm font-medium text-gray-600">
          Contract Images
        </label>
        {images.map((img, index) => (
          <div key={index} className="flex items-center gap-3">
            <UploadImage
              onFileSelect={(file) => handleImageSelect(index, file)}
              preview={img.preview}
            />
            {images.length > 1 && (
              <button
                type="button"
                onClick={() => removeImageRow(index)}
                className="text-red-500 hover:text-red-700"
              >
                <HiTrash className="w-5 h-5" />
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addImageRow}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
        >
          <HiPlus className="w-4 h-4" /> Add another image
        </button>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-lg bg-green-500 py-2.5 text-white font-medium
               hover:bg-green-700 transition"
      >
        {loading ? "Saving..." : "Add Contract"}
      </button>
    </form>
  );
}
